import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchCount } from '@app/src/api/fetchHooks'
import { useGetFormattedDate } from '@app/src/components/DateDisplay'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import LevelSquareBadge from '@app/src/components/LevelSquareBadge'
import ListItemSkeleton from '@app/src/components/Skeleton/ListItem'
import Avatar, { AvatarSize } from '@app/src/components/Ui/Avatar'
import useAssessmentQuestionnaireLink from '@app/src/hooks/useAssessmentQuestionnaireLink'
import useProviderType from '@app/src/hooks/useProviderType'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { AllTabs } from '@app/src/pages/ResourceExplorer/Content'
import type Assessment from '@app/src/types/assessment'
import { SortOrder } from '@app/src/types/filter'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import paths from '@app/src/wf-constants/paths'
import { ChevronRightOutlined, ScheduleOutlined } from '@mui/icons-material'
import { ListItem, ListItemAvatar, ListItemButton, ListItemText, Stack, alpha } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { Link, generatePath } from 'react-router-dom'

type BaselineAssessmentOverviewProps = {
  providerId: number
}

const BaselineAssessmentOverview: React.FC<BaselineAssessmentOverviewProps> = ({ providerId }) => {
  const { formatMessage } = useIntl()
  const { closeDrawer } = useDrawer()
  const providerType = useProviderType()
  const { formatValueToDate } = useGetFormattedDate()
  const { assessmentQuestionnaireLinks } = useAssessmentQuestionnaireLink({
    assessmentType: AssessmentType.BaselineAssessment,
    includeAssessment: true,
  })

  const baselineAssessmentQuestionnaireTemplateId = assessmentQuestionnaireLinks?.find(
    link => link.assessmentTemplate?.assessmentType === AssessmentType.BaselineAssessment,
  )?.questionnaireTemplateId

  const {
    items: [latestAssessment],
    isLoading: isLoadingLatestAssessment,
  } = useFetchCollectionWithPost<Assessment>({
    key: FetchKey.AssessmentCollection,
    endpoint: endpoints.assessmentCollection,
    payload: {
      sort: {
        order: SortOrder.DESCENDING,
        target: 'periodName',
      },
      filter: [
        {
          name: 'providerId',
          filters: [
            {
              operator: Operators.EqualTo,
              value: providerId,
            },
          ],
        },
        {
          name: 'assessmentTemplate.questionnaireTemplateId',
          filters: [
            {
              operator: Operators.EqualTo,
              value: baselineAssessmentQuestionnaireTemplateId,
            },
          ],
        },
      ],
      include: ['response', 'provider.providerOwners.user'],
      pagination: {
        pageNumber: 1,
        itemsPerPage: 1,
      },
    },
    options: {
      enabled: Boolean(baselineAssessmentQuestionnaireTemplateId),
    },
  })

  const { count: baselineAssessmentInquiryCount, isLoading: isLoadingBaselineAssessmentInquiryCount } = useFetchCount({
    key: FetchKey.InquiryCount,
    endpoint: endpoints.inquiryCount,
    payload: [
      {
        name: 'providerId',
        filters: [
          {
            operator: Operators.EqualTo,
            value: providerId,
          },
        ],
      },
      {
        name: 'questionnaireTemplateId',
        filters: [
          {
            operator: Operators.EqualTo,
            value: baselineAssessmentQuestionnaireTemplateId,
          },
        ],
      },
    ],

    options: {
      enabled: !isLoadingLatestAssessment && !latestAssessment && Boolean(baselineAssessmentQuestionnaireTemplateId),
    },
  })

  if (isLoadingLatestAssessment || isLoadingBaselineAssessmentInquiryCount) {
    return (
      <ListItemSkeleton
        avatar
        listItemAvatarSx={{ minWidth: 64 }}
        avatarProps={{ size: AvatarSize.XL, sx: ({ palette }) => ({ bgcolor: alpha(palette.common.black, 0.04) }) }}
        primary
        secondary
      />
    )
  }

  if (!latestAssessment && !baselineAssessmentInquiryCount) {
    return null
  }

  return (
    <ListItem disablePadding sx={{ color: 'text.primary' }}>
      <ListItemButton
        sx={{
          py: 0.5,
        }}
        disabled={!latestAssessment}
        component={Link}
        onClick={closeDrawer}
        to={generatePath(paths.resourceExplorer, {
          resourceType: providerType,
          resourceId: providerId,
          activeTabParam: AllTabs.Assessments,
        })}
      >
        <ListItemAvatar sx={{ minWidth: 64 }}>
          <Avatar
            size={AvatarSize.XL}
            sx={({ palette }) => ({
              bgcolor: palette.common.white,
              border: latestAssessment ? '' : `1px solid ${alpha(palette.common.black, 0.12)}`,
            })}
          >
            {latestAssessment ? (
              <Stack alignItems="center">
                <LevelSquareBadge
                  value={latestAssessment.totalLevel}
                  levelsCount={latestAssessment.assessmentTemplate?.levels}
                />
              </Stack>
            ) : (
              <ScheduleOutlined color="disabled" />
            )}
          </Avatar>
        </ListItemAvatar>

        <ListItemText
          primary={formatMessage({ id: 'navbar.baselineAssessment' })}
          secondary={
            latestAssessment
              ? formatMessage({ id: 'baselineAssessment.providerPage.resultsAssessedOn' }) +
                ' ' +
                formatValueToDate({ value: latestAssessment?.updatedAt ?? latestAssessment.createdAt })
              : formatMessage({ id: 'baselineAssessment.infoDialog.pendingInfo' })
          }
        />

        <ChevronRightOutlined color="action" />
      </ListItemButton>
    </ListItem>
  )
}

export default BaselineAssessmentOverview
